import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
} from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useSelector } from "react-redux";
import { useTranslation, translateHeaders } from "../../../utils/useTranslation";
import { chinaShipmentTranslationMap } from "../../../utils/translations";
import { DataTableWithPagination } from "../../../components/custom/DataTable/DataTableWithPagination";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import moment from "moment";
import { Provider as PaperProvider, DefaultTheme, DataTable } from "react-native-paper";
import { DatePickerModal, registerTranslation } from "react-native-paper-dates";
import { getAllDeliveryAttachments } from "../../../services/deliveryService";
import { DeliveryDispatcher } from "../../../redux/dispatcher/deliveryDispatcher";
import { actionTypesDelivery } from "../../../redux/constants/actionTypesDelivery";

interface DeliveryRecentUploadsModalProps {
  visible: boolean;
  onClose: () => void;
  onRowClick?: (row: any) => void;
  deliveryList: any[];
}

const headers = {
  docNum: {
    name: "Document No",
    width: 0.2,
  },
  uploadedBy: {
    name: "Uploaded By",
    width: 0.4,
  },
  createdAt: {
    name: "Created Date",
    width: 0.4,
  },
};

const recentUploadsTranslationMap: Record<string, any> = {
  docNum: "documentNo",
  uploadedBy: "uploadedBy",
  createdAt: "createdDate",
};

const DeliveryRecentUploadsModal: React.FC<DeliveryRecentUploadsModalProps> = ({
  visible,
  onClose,
  onRowClick,
  deliveryList,
}) => {
  const listRef = useRef<ScrollView>(null);
  const { t } = useTranslation();

  const { isLargeDevice, isSmallDevice, isMediumDevice } = useContext(
    DeviceOrientationContext,
  );

  const { recentUploads } = useSelector(
    (state: any) => state.delivery,
  );

  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [sortDirection, setSortDirection] = useState<"recent" | "oldest">("recent");
  const [dateRangePickerOpen, setDateRangePickerOpen] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<{
    startDate: Date | undefined;
    endDate: Date | undefined;
  }>({ startDate: undefined, endDate: undefined });

  const [dateFilterPreset, setDateFilterPreset] = useState<"all" | "last7days" | "yesterday" | "today" | "custom">("all");
  const [filterOpen, setFilterOpen] = useState<"date" | null>(null);
  const dateFilters = ["all", "today", "yesterday", "last7days", "custom"];

  const fetchData = () => {
    getAllDeliveryAttachments()
      .then((response: any) => {
        if (response && response.data) {
          let dataArray = Array.isArray(response.data) ? response.data : (response.data.data || []);
          
          if (Array.isArray(dataArray) && dataArray.length > 0) {
            const mapped = dataArray.map((item: any) => ({
              docNum: item.DocNum,
              files: item.attachment ? item.attachment.split(",") : [],
              createdAt: item.createdAt,
              updatedAt: item.updatedAt,
              uploadedBy: "Unknown",
            }));
            
            // Merge server uploads with any local ones so we don't wipe out local state
            const merged = [...(recentUploads || [])];
            mapped.forEach((serverItem: any) => {
              const existingIndex = merged.findIndex((localItem: any) => String(localItem.docNum) === String(serverItem.docNum));
              if (existingIndex === -1) {
                merged.push(serverItem);
              } else {
                merged[existingIndex] = serverItem;
              }
            });
            DeliveryDispatcher(actionTypesDelivery.SetRecentUploads, merged);
          }
        }
      })
      .catch((err: any) => {
        console.error("Failed to fetch global delivery attachments", err);
      });
  };

  useEffect(() => {
    if (visible) {
      fetchData();
    }
  }, [visible]);

  const formatShortDate = (d: Date) => {
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getDateRangeLabel = () => {
    switch (dateFilterPreset) {
      case "all": return t("dateAll") || "Date: All";
      case "last7days": return t("last7days") || "Last 7 Days";
      case "yesterday": return t("yesterday") || "Yesterday";
      case "today": return t("today") || "Today";
      case "custom":
        if (customDateRange.startDate && customDateRange.endDate) {
          return `${formatShortDate(customDateRange.startDate)} ${t("to") || "to"} ${formatShortDate(customDateRange.endDate)}`;
        }
        return t("dateRange") || "Date Range";
      default: return t("dateAll") || "Date: All";
    }
  };

  const formattedUploads = useMemo(() => {
    let result = (recentUploads || []).map((u: any) => {
      const deliveryItem = deliveryList?.find((i: any) => String(i.DocNum) === String(u.docNum));
      return {
        docNum: u.docNum,
        uploadedBy: u.uploadedBy || "Unknown",
        createdAt: u.createdAt || u.updatedAt,
        updatedAt: u.updatedAt,
        state: deliveryItem?.State || "-", // Keep state for filtering purposes but it's not in headers
      };
    });

    if (searchText && searchText.trim()) {
      const query = searchText.toLowerCase();
      result = result.filter(
        (u: any) =>
          String(u.docNum).toLowerCase().includes(query) ||
          String(u.uploadedBy || "").toLowerCase().includes(query)
      );
    }

    result.sort((a: any, b: any) => {
      const dateA = new Date(a.updatedAt).getTime();
      const dateB = new Date(b.updatedAt).getTime();
      return sortDirection === "recent" ? dateB - dateA : dateA - dateB;
    });

    if (dateFilterPreset !== "all") {
      result = result.filter((u: any) => {
        const itemDateStr = u.createdAt || u.updatedAt;
        if (!itemDateStr) return false;
        const itemDate = new Date(itemDateStr);
        itemDate.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (dateFilterPreset === "today") {
          return itemDate.getTime() === today.getTime();
        }
        if (dateFilterPreset === "yesterday") {
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          return itemDate.getTime() === yesterday.getTime();
        }
        if (dateFilterPreset === "last7days") {
          const last7 = new Date(today);
          last7.setDate(last7.getDate() - 7);
          return itemDate >= last7 && itemDate <= today;
        }
        if (dateFilterPreset === "custom" && customDateRange.startDate && customDateRange.endDate) {
          return itemDate >= customDateRange.startDate! && itemDate <= customDateRange.endDate!;
        }
        return true;
      });
    }

    return result;
  }, [recentUploads, deliveryList, searchText, sortDirection, customDateRange.startDate, customDateRange.endDate, dateFilterPreset]);

  const handleSearch = (text: string) => {
    setPage(0);
    setSearchText(text);
  };

  const getModalWidth = () => {
    if (isSmallDevice) return "100%";
    if (isMediumDevice) return "94%";
    return "92%";
  };

  const translatedHeaders = useMemo(() => translateHeaders(
    headers,
    t,
    recentUploadsTranslationMap,
  ), [t]);

  const tableContent = (
    <View>
      <DataTableWithPagination
        isToExpandAllRows={false}
        isRowsExpandable={false}
        headers={translatedHeaders}
        rows={formattedUploads}
        itemsPerPage={10}
        isDisplayTimeWithDate={true}
        useModal={true}
        isDetail={false}
        isEdit={false}
        isDelete={false}
        actionRows={[]}
        setActionRows={() => { }}
        itemTracker={false}
        setModalVisible={() => { }}
        handleRowClick={(row) => onRowClick && onRowClick(row)}
        setSelected={() => { }}
        handleDeleteRow={() => { }}
        t={t}
        page={page}
        setPage={setPage}
        handleSortColumn={() => { }}
        operationActions={(row) => onRowClick && onRowClick(row)}
        rowKeyValue="docNum"
        columnsToNotHaveThousandSeparator={["Document No", "docNum", "Request Date", t("documentNo")]}
        hidePagination={true}
      />
    </View>
  );

  const titleAndSearch = (
    <View
      style={[
        styles.titleContainer,
        isSmallDevice && { flexDirection: "column" },
      ]}
    >
      <Text style={[styles.titleText, isSmallDevice && styles.titleTextSmall]}>
        {t("recentUploads") || "Recent Uploads"} ({formattedUploads.length})
      </Text>
      <View style={styles.searchContainer}>
        <LabelBannerWithSearchBar
          titleName={``}
          handleSearch={handleSearch}
          setRefresh={fetchData}
          dataToExport={formattedUploads}
          fileName="RecentUploads"
          sheetName="Recent Uploads"
        />
      </View>
    </View>
  );

  const filterBar = (
    <View style={styles.filterBarContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filterBarContent}>
        <TouchableOpacity onPress={() => setSortDirection("recent")}>
          <Text
            style={
              sortDirection === "recent"
                ? styles.selectedFilter
                : styles.filterLabel
            }
          >
            ↓ {t("recent")}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => setSortDirection("oldest")}>
          <Text
            style={
              sortDirection === "oldest"
                ? styles.selectedFilter
                : styles.filterLabel
            }
          >
            ↑ {t("oldest")}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity onPress={() => setFilterOpen(filterOpen === "date" ? null : "date")}>
          <Text
            style={
              dateFilterPreset !== "all"
                ? styles.selectedFilter
                : styles.filterLabel
            }
          >
            {getDateRangeLabel()} {filterOpen === "date" ? "▲" : "▼"}
          </Text>
        </TouchableOpacity>
        {dateFilterPreset === "custom" && customDateRange.startDate && customDateRange.endDate && (
          <TouchableOpacity onPress={() => { setCustomDateRange({ startDate: undefined, endDate: undefined }); setDateFilterPreset("all"); }}>
            <Text style={styles.filterLabel}>✕</Text>
          </TouchableOpacity>
        )}
        </ScrollView>

      {filterOpen === "date" && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={[styles.filterBarContent, { marginTop: 14 }]}>
          {dateFilters.map((df) => {
            const isActive = dateFilterPreset === df;
            let label = t("all") || "All";
            if (df === "today") label = t("today") || "Today";
            if (df === "yesterday") label = t("yesterday") || "Yesterday";
            if (df === "last7days") label = t("last7days") || "Last 7 Days";
            if (df === "custom") label = t("dateRange") || "Date Range";

            return (
              <TouchableOpacity
                key={df}
                onPress={() => {
                  if (df === "custom") {
                    setDateRangePickerOpen(true);
                  }
                  setDateFilterPreset(df as any);
                }}
              >
                <Text style={isActive ? styles.selectedFilter : styles.filterLabel}>{label}</Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      )}
    </View>
  );

  const pagination = formattedUploads.length > 0 ? (
    <DataTable.Pagination
      page={page}
      numberOfPages={Math.ceil(formattedUploads.length / 10)}
      onPageChange={(page) => setPage(page)}
      label={`${Math.min((page + 1) * 10, formattedUploads.length)} ${t("of")} ${formattedUploads.length}`}
      showFastPaginationControls
      numberOfItemsPerPage={10}
      style={{ alignSelf: "flex-end", marginRight: 16 }}
    />
  ) : null;

  const footer = (
    <View style={[styles.footer, isSmallDevice && styles.footerSmall]}>
      <TouchableOpacity
        onPress={onClose}
        style={[styles.closeButton, isSmallDevice && styles.buttonSmall]}
      >
        <Text style={isSmallDevice && styles.buttonTextSmall}>
          {t("close")}
        </Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <>
      <Modal
        transparent={true}
        visible={visible}
        animationType="fade"
        onRequestClose={onClose}
      >
        <View style={styles.overlay}>
          {isLargeDevice ? (
            <View style={[styles.modalContainer, { width: getModalWidth() }]}>
              <View
                style={[
                  styles.modalContainerSecond,
                  { width: "100%", height: "100%" },
                ]}
              >
                {titleAndSearch}
                {filterBar}
                <ScrollView ref={listRef}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator
                    contentContainerStyle={{
                      minWidth: "100%",
                    }}
                  >
                    <View
                      style={[
                        styles.containerContent,
                        { minWidth: "100%" },
                      ]}
                    >
                      {tableContent}
                    </View>
                  </ScrollView>
                </ScrollView>
                {pagination}
                {footer}
              </View>
            </View>
          ) : (
            <View
              style={[
                styles.modalContainerSecond,
                { width: "100%", height: "90%" },
              ]}
            >
              {titleAndSearch}
              {filterBar}
              <ScrollView ref={listRef}>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator
                  contentContainerStyle={{
                    minWidth: "100%",
                  }}
                >
                  <View
                    style={[
                      styles.containerContent,
                      { minWidth: "100%" },
                    ]}
                  >
                    {tableContent}
                  </View>
                </ScrollView>
              </ScrollView>
              {pagination}
              {footer}
            </View>
          )}
        </View>
      </Modal>

      {/* @ts-ignore: DatePickerModal typing conflict */}
      <PaperProvider
        theme={{
          ...DefaultTheme,
          colors: {
            ...DefaultTheme.colors,
            primary: "#3B82F6",
            primaryContainer: "#DBEAFE",
            onPrimaryContainer: "#1E40AF",
            secondaryContainer: "#BFDBFE",
            onSecondaryContainer: "#1E3A5F",
            onSurface: "#1E293B",
            onSurfaceVariant: "#475569",
          },
        }}
      >
        <DatePickerModal
          locale="en-AU"
          mode="range"
          visible={dateRangePickerOpen}
          onDismiss={() => setDateRangePickerOpen(false)}
          startDate={customDateRange.startDate}
          endDate={customDateRange.endDate}
          onConfirm={({ startDate, endDate }: any) => {
            setDateRangePickerOpen(false);
            const finalEndDate = endDate || startDate;
            if (startDate && finalEndDate) {
              startDate.setHours(0, 0, 0, 0);
              finalEndDate.setHours(0, 0, 0, 0);
              setCustomDateRange({ startDate, endDate: finalEndDate });
            }
          }}
        />
      </PaperProvider>
    </>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.15)",
    paddingVertical: 16,
    paddingHorizontal: 8,
  },
  modalContainer: {
    backgroundColor: "#fff",
    padding: 20,
    elevation: 12,
    borderRadius: 12,
    width: "100%",
    maxHeight: "95%",
  },
  modalContainerSecond: {
    backgroundColor: "#fff",
    elevation: 12,
    borderRadius: 12,
    padding: 16,
    width: "100%",
    height: "95%",
  },
  titleContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
    paddingVertical: 12,
    marginBottom: 12,
  },
  titleText: {
    fontSize: 18,
    fontWeight: "700",
  },
  titleTextSmall: {
    fontSize: 14,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  filterBarContainer: {
    marginBottom: 10,
    paddingHorizontal: 4,
  },
  filterBarContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  filterLabel: {
    fontSize: 12,
    fontWeight: "500",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginHorizontal: 4,
    backgroundColor: "#F1F5F9",
    color: "#4B5563",
    overflow: "hidden",
  },
  selectedFilter: {
    fontSize: 12,
    fontWeight: "600",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginHorizontal: 4,
    backgroundColor: "#56B7FD",
    color: "#fff",
    overflow: "hidden",
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
    width: "100%",
    paddingHorizontal: 0,
    paddingBottom: 0,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginTop: 20,
    padding: 25,
  },
  footerSmall: {
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  closeButton: {
    padding: 10,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
    width: 120,
    alignItems: "center",
  },
  buttonSmall: {
    padding: 8,
    paddingHorizontal: 12,
  },
  buttonTextSmall: {
    fontSize: 12,
  },
});

export default DeliveryRecentUploadsModal;
